import React from 'react';
import './PrivacyPage.css';

interface PrivacyPageProps {
  onBackToHome: () => void;
}

const PrivacyPage: React.FC<PrivacyPageProps> = ({ onBackToHome }) => {
  return (
    <div className="privacy-page">
      {/* Header */}
      <header className="privacy-page__header">
        <div className="privacy-page__container">
          <button className="privacy-page__back-button" onClick={onBackToHome}>
            ← Back to Home
          </button>
          <div className="privacy-page__header-content">
            <h1 className="privacy-page__title">Privacy Policy</h1>
            <p className="privacy-page__subtitle">
              Your privacy and data protection are our top priorities
            </p>
            <div className="privacy-page__last-updated">
              Last updated: April 5, 2026
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="privacy-page__content">
        <div className="privacy-page__container">
          <div className="privacy-section">
            <h2>Introduction</h2>
            <p>
              Faathum ("we," "our," or "us") is committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our platform that advances how data is understood 
              platform and widgets.
            </p>
            <p>
              By using Faathum's services, you agree to the collection and use of information in 
              accordance with this policy. We will not use or share your information with anyone 
              except as described in this Privacy Policy.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Information We Collect</h2>
            
            <h3>Information You Provide Directly</h3>
            <ul>
              <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
              <li><strong>Widget Configuration:</strong> Dataset metadata, descriptions, and configuration settings you provide when creating widgets.</li>
              <li><strong>Support Communications:</strong> Information you provide when contacting our support team.</li>
              <li><strong>Feedback and Surveys:</strong> Responses to surveys, feedback forms, or user research sessions.</li>
            </ul>

            <h3>Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Analytics:</strong> How you interact with our platform, including pages visited, features used, and time spent.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers.</li>
              <li><strong>Widget Analytics:</strong> Aggregated usage statistics for widgets deployed on websites (no personal identification).</li>
              <li><strong>Log Data:</strong> Server logs including access times, pages viewed, and technical error information.</li>
            </ul>

            <h3>Information from Third Parties</h3>
            <ul>
              <li><strong>AI Platform Integration:</strong> We do not store or access your interactions with third-party AI platforms (ChatGPT, Claude, etc.).</li>
              <li><strong>Authentication Services:</strong> If you sign in through third-party services, we receive basic profile information as permitted by your settings.</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>How We Use Your Information</h2>
            
            <div className="privacy-use-cases">
              <div className="privacy-use-case">
                <h4>🛠️ Service Provision</h4>
                <ul>
                  <li>Create and manage your account</li>
                  <li>Generate and deploy AI exploration widgets</li>
                  <li>Provide customer support and technical assistance</li>
                  <li>Process payments and manage subscriptions</li>
                </ul>
              </div>
              
              <div className="privacy-use-case">
                <h4>📊 Analytics and Improvement</h4>
                <ul>
                  <li>Analyze usage patterns to improve our platform</li>
                  <li>Monitor system performance and reliability</li>
                  <li>Conduct research and development for new features</li>
                  <li>Generate aggregated, anonymized usage statistics</li>
                </ul>
              </div>
              
              <div className="privacy-use-case">
                <h4>📧 Communication</h4>
                <ul>
                  <li>Send important service updates and notifications</li>
                  <li>Respond to your inquiries and support requests</li>
                  <li>Share product updates and new features (with consent)</li>
                  <li>Conduct user research and feedback collection</li>
                </ul>
              </div>
              
              <div className="privacy-use-case">
                <h4>🔒 Security and Compliance</h4>
                <ul>
                  <li>Protect against fraud, abuse, and security threats</li>
                  <li>Comply with legal obligations and regulatory requirements</li>
                  <li>Enforce our Terms of Service and other policies</li>
                  <li>Investigate and resolve technical issues</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="privacy-section">
            <h2>Information Sharing and Disclosure</h2>
            
            <div className="privacy-sharing-policy">
              <div className="privacy-no-sale">
                <h3>🚫 We Do Not Sell Your Data</h3>
                <p>
                  Faathum does not sell, rent, or trade your personal information to third parties 
                  for marketing or advertising purposes. Your data is not a product.
                </p>
              </div>
              
              <h3>Limited Sharing Scenarios</h3>
              <ul>
                <li><strong>Service Providers:</strong> Trusted third-party services that help us operate our platform (hosting, analytics, payment processing) under strict data protection agreements.</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and the safety of our users.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, with advance notice to users.</li>
                <li><strong>Consent:</strong> With your explicit consent for specific purposes not covered in this policy.</li>
              </ul>
            </div>
          </div>

          <div className="privacy-section">
            <h2>AI Platform Integration</h2>
            
            <div className="privacy-ai-section">
              <h3>How AI Integration Works</h3>
              <p>
                When you use our widgets to explore data with AI platforms, here's what happens:
              </p>
              <ol>
                <li>Our widget generates a contextual prompt based on your configuration</li>
                <li>You are redirected to the AI platform of your choice with the pre-filled prompt</li>
                <li>Your interaction occurs directly with the AI platform, not through Faathum</li>
                <li>We do not see, store, or have access to your conversations with AI platforms</li>
              </ol>
              
              <div className="privacy-ai-disclaimer">
                <h4>⚠️ Important AI Platform Notice</h4>
                <p>
                  Each AI platform (ChatGPT, Claude, Gemini, etc.) has its own privacy policy 
                  and data handling practices. When you interact with these platforms, you are 
                  subject to their terms and policies, not ours. We recommend reviewing their 
                  privacy policies before use.
                </p>
              </div>
            </div>
          </div>

          <div className="privacy-section">
            <h2>Data Security</h2>
            
            <div className="privacy-security-measures">
              <div className="privacy-security-item">
                <h4>🔐 Encryption</h4>
                <p>All data transmission is encrypted using industry-standard TLS protocols. Sensitive data is encrypted at rest.</p>
              </div>
              
              <div className="privacy-security-item">
                <h4>🏢 Infrastructure Security</h4>
                <p>Our platform is hosted on secure, SOC 2 compliant infrastructure with regular security audits and monitoring.</p>
              </div>
              
              <div className="privacy-security-item">
                <h4>👥 Access Controls</h4>
                <p>Strict access controls ensure only authorized personnel can access user data, and only when necessary for service provision.</p>
              </div>
              
              <div className="privacy-security-item">
                <h4>🔄 Regular Updates</h4>
                <p>We regularly update our security measures and conduct vulnerability assessments to protect against emerging threats.</p>
              </div>
            </div>
            
            <div className="privacy-security-notice">
              <p>
                While we implement robust security measures, no system is 100% secure. We encourage 
                users to use strong passwords and report any suspicious activity immediately.
              </p>
            </div>
          </div>

          <div className="privacy-section">
            <h2>Your Rights and Choices</h2>
            
            <div className="privacy-rights-grid">
              <div className="privacy-right">
                <h4>📋 Access</h4>
                <p>Request a copy of the personal information we have about you.</p>
              </div>
              
              <div className="privacy-right">
                <h4>✏️ Correction</h4>
                <p>Update or correct inaccurate personal information in your account.</p>
              </div>
              
              <div className="privacy-right">
                <h4>🗑️ Deletion</h4>
                <p>Request deletion of your account and associated personal data.</p>
              </div>
              
              <div className="privacy-right">
                <h4>📤 Portability</h4>
                <p>Export your widget configurations and account data in a portable format.</p>
              </div>
              
              <div className="privacy-right">
                <h4>🚫 Opt-Out</h4>
                <p>Unsubscribe from marketing communications and optional data collection.</p>
              </div>
              
              <div className="privacy-right">
                <h4>⏸️ Restriction</h4>
                <p>Limit how we process your personal information in certain circumstances.</p>
              </div>
            </div>
            
            <div className="privacy-rights-contact">
              <p>
                To exercise any of these rights, contact us at <strong>privacy@faathum.com</strong>. 
                We will respond to your request within 30 days.
              </p>
            </div>
          </div>

          <div className="privacy-section">
            <h2>Data Retention</h2>
            
            <ul>
              <li><strong>Account Data:</strong> Retained while your account is active and for 90 days after deletion to allow for account recovery.</li>
              <li><strong>Widget Configurations:</strong> Retained as long as widgets are deployed or until you delete them.</li>
              <li><strong>Usage Analytics:</strong> Aggregated, anonymized data may be retained indefinitely for service improvement.</li>
              <li><strong>Support Communications:</strong> Retained for 3 years to provide ongoing support and improve our services.</li>
              <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law or for legitimate business purposes.</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>International Data Transfers</h2>
            
            <p>
              Faathum operates globally and may transfer your information to countries other than 
              where you reside. We ensure appropriate safeguards are in place for international 
              transfers, including:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by relevant data protection authorities</li>
              <li>Adequacy decisions by the European Commission where applicable</li>
              <li>Other legally recognized transfer mechanisms</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>Children's Privacy</h2>
            
            <p>
              Faathum is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian 
              and believe your child has provided us with personal information, please contact us 
              immediately so we can delete such information.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Changes to This Policy</h2>
            
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our 
              practices, technology, legal requirements, or other factors. We will:
            </p>
            <ul>
              <li>Notify you of material changes via email or prominent notice on our platform</li>
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>Provide at least 30 days notice before material changes take effect</li>
              <li>Obtain your consent for changes that expand our use of your personal information</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>Contact Us</h2>
            
            <div className="privacy-contact-info">
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or 
                our data practices, please contact us:
              </p>
              
              <div className="privacy-contact-methods">
                <div className="privacy-contact-method">
                  <strong>📧 Email:</strong> privacy@faathum.com
                </div>
                <div className="privacy-contact-method">
                  <strong>📮 Mail:</strong> Faathum Privacy Team<br />
                  [Company Address]<br />
                  [City, State, ZIP Code]
                </div>
                <div className="privacy-contact-method">
                  <strong>🌐 Online:</strong> Submit a request through our support portal
                </div>
              </div>
              
              <p>
                We are committed to resolving privacy concerns promptly and transparently. 
                You also have the right to lodge a complaint with your local data protection authority.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
